import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { CompanyFormData } from "./company-form-types";

interface CompanyAppearanceSectionProps {
	form: CompanyFormData;
	setForm: React.Dispatch<React.SetStateAction<CompanyFormData>>;
	logoUrl: string | undefined;
	logoInputRef: RefObject<HTMLInputElement | null>;
	isUploading: boolean;
	onLogoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function CompanyAppearanceSection({
	form,
	setForm,
	logoUrl,
	logoInputRef,
	isUploading,
	onLogoChange,
}: CompanyAppearanceSectionProps) {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-3">
			<p className="text-sm font-medium text-muted-foreground">
				{t("settings.company.appearance")}
			</p>

			<div className="grid gap-2">
				<Label>{t("settings.company.logo")}</Label>
				{logoUrl && (
					<img
						src={logoUrl}
						alt={t("settings.company.logo")}
						className="h-12 w-auto object-contain rounded border p-1.5"
					/>
				)}
				<input
					ref={logoInputRef}
					type="file"
					accept="image/*"
					className="hidden"
					onChange={onLogoChange}
				/>
				<Button
					variant="outline"
					size="sm"
					className="w-fit"
					disabled={isUploading}
					onClick={() => logoInputRef.current?.click()}
				>
					{isUploading
						? t("settings.company.uploading")
						: t("settings.company.chooseFile")}
				</Button>
			</div>

			<div className="flex gap-6">
				<div className="grid gap-2">
					<Label htmlFor="primary-color">
						{t("settings.company.primaryColor")}
					</Label>
					<div className="flex items-center gap-2">
						<input
							id="primary-color"
							type="color"
							value={form.primary_color}
							onChange={(e) =>
								setForm((f) => ({ ...f, primary_color: e.target.value }))
							}
							className="size-9 cursor-pointer rounded border p-0.5"
						/>
						<span className="text-sm text-muted-foreground font-mono">
							{form.primary_color}
						</span>
					</div>
				</div>
				<div className="grid gap-2">
					<Label htmlFor="secondary-color">
						{t("settings.company.secondaryColor")}
					</Label>
					<div className="flex items-center gap-2">
						<input
							id="secondary-color"
							type="color"
							value={form.secondary_color}
							onChange={(e) =>
								setForm((f) => ({
									...f,
									secondary_color: e.target.value,
								}))
							}
							className="size-9 cursor-pointer rounded border p-0.5"
						/>
						<span className="text-sm text-muted-foreground font-mono">
							{form.secondary_color}
						</span>
					</div>
				</div>
			</div>

			<div className="flex items-center gap-2">
				<Checkbox
					id="hide-electricity"
					checked={form.hide_electricity}
					onCheckedChange={(checked) =>
						setForm((f) => ({ ...f, hide_electricity: checked === true }))
					}
				/>
				<Label htmlFor="hide-electricity">
					{t("settings.company.hideElectricity")}
				</Label>
			</div>
		</div>
	);
}
